import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getCurrencyById, createCurrency, updateCurrency } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import ToggleSwitch from "../components/UI/ToggleSwitch";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

export default function AddEditCurrency() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t } = useTranslation("currencies");
  const { t: tCommon } = useTranslation("common");

  const [isBase, setIsBase] = useState(false);
  const [form, setForm] = useState({
    code: "",
    name: "",
    symbol: "",
    exchange_rate: "1",
    decimal_places: "2",
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (id) {
      getCurrencyById(token, id).then((c) => {
        setIsBase(!!c.is_base);
        setForm({
          code: c.code || "",
          name: c.name || "",
          symbol: c.symbol || "",
          exchange_rate: c.exchange_rate ?? "1",
          decimal_places: c.decimal_places ?? "2",
          is_active: c.is_active !== undefined ? c.is_active : true,
        });
      }).catch(() => notify.error(t("notify_load_failed", "Failed to load currency")));
    }
  }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async () => {
    setErrors({});
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = t("validation_name_required", "Currency name is required");
    if (form.code.trim().length !== 3) newErrors.code = t("validation_code_length", "Code must be exactly 3 letters (e.g. EGP, USD)");
    if (form.exchange_rate === "" || Number(form.exchange_rate) <= 0) newErrors.exchange_rate = t("validation_rate_positive", "Enter a rate greater than zero");
    const dp = Number(form.decimal_places);
    if (form.decimal_places === "" || dp < 0 || dp > 4) newErrors.decimal_places = t("validation_decimal_places_range", "Must be between 0 and 4");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      notify.error(tCommon("validation_fill_required_fields", "Please fill in the required fields correctly"));
      return;
    }

    setLoading(true);
    const payload = {
      code: form.code.trim().toUpperCase(),
      name: form.name.trim(),
      symbol: form.symbol.trim(),
      exchange_rate: Number(form.exchange_rate),
      decimal_places: Number(form.decimal_places),
      // is_base is never set from this form — it's only ever changed via the
      // dedicated "Set as Base" action on the Currencies list, so an existing
      // base currency's status is preserved as-is on every update here.
      is_base: isBase,
      is_active: form.is_active,
    };

    try {
      if (id) {
        await updateCurrency(token, id, payload);
        notify.success(t("notify_updated", "Currency updated successfully"));
      } else {
        await createCurrency(token, payload);
        notify.success(t("notify_created", "Currency created successfully"));
      }
      navigate("/currencies");
    } catch (err) {
      notify.error(err.data?.error || err.message || tCommon("error_generic", "Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/currencies")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">{id ? t("edit_title", "Edit Currency") : t("add_title", "Add New Currency")}</h2>
          <p className="text-gray-400 text-sm italic font-medium">{t("form_subtitle", "Use \"Set as Base\" on the Currencies list to make this the reference currency")}</p>
        </div>
      </PageHeader>

      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div className="space-y-6">
          {isBase && (
            <div className="px-4 py-3 rounded-2xl bg-blue-50 text-blue-700 text-sm font-medium">
              {t("base_currency_banner", "This is the base currency — its exchange rate is always locked to 1.")}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <FloatingInput label={t("field_code_label", "Code (e.g. EGP)")} value={form.code} onChange={setField("code")} error={errors.code} icon={ICONS.Currencies} />
            <FloatingInput label={t("field_symbol_label", "Symbol (e.g. $)")} value={form.symbol} onChange={setField("symbol")} />
          </div>

          <FloatingInput label={t("field_name_label", "Currency Name")} value={form.name} onChange={setField("name")} error={errors.name} />

          <div className="grid grid-cols-2 gap-4">
            <FloatingInput
              label={t("field_exchange_rate_label", "Exchange Rate vs Base")}
              type="number"
              value={isBase ? "1" : form.exchange_rate}
              onChange={setField("exchange_rate")}
              error={errors.exchange_rate}
              disabled={isBase}
            />
            <FloatingInput label={t("field_decimal_places_label", "Decimal Places")} type="number" value={form.decimal_places} onChange={setField("decimal_places")} error={errors.decimal_places} />
          </div>

          <ToggleSwitch
            label={tCommon("status_active", "Active")}
            checked={form.is_active}
            onChange={(val) => setForm((prev) => ({ ...prev, is_active: val }))}
          />

          <div className="flex gap-4 pt-6 border-t border-gray-50">
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              className="flex-grow shadow-lg shadow-blue-500/10"
              icon={id ? ICONS.Save : ICONS.Plus}
            >
              {id ? t("btn_update_currency", "Update Currency") : t("btn_create_currency", "Create Currency")}
            </Button>
            <Button variant="secondary" onClick={() => navigate("/currencies")}>{tCommon("btn_cancel", "Cancel")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
